import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, ChevronDown, BookOpen, Lock, Crown, Plus, Minus, Check, ArrowLeft } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useAdmin } from '@/hooks/useAdmin';
import { getEntitlements, isSelfLearnActive } from '@/utils/entitlements';
import { useOrgVocab } from '@/contexts/SubdomainBrandingContext';
import { PlanBadge } from '@/components/subscription/PlanBadge';
import CourseDetailModal, { ELITE_COURSE_DETAILS, CourseDetail } from '@/components/elite/CourseDetailModal';

interface MasterClass {
  id: string;
  name: string;
  description: string | null;
  target_age: number | null;
  category_id: string;
  category_name: string;
  subject_group: string;
  is_enrolled: boolean;
  curriculum_track: string | null;
  lesson_count?: number | null;
  module_count?: number | null;
  module_plan?: any[] | null;
}

const isElite = (c: { subject_group?: string; curriculum_track?: string | null }) =>
  c.subject_group === 'Elite Academy' ||
  ['foundation', 'intermediate', 'advanced'].includes(c.curriculum_track || '');

const isFaith = (c: { curriculum_track?: string | null }) => c.curriculum_track === 'faith';

const GROUP_ICONS: Record<string, string> = {
  'Mathematics': '📐',
  'Sciences': '🔬',
  'English & Writing': '📝',
  'Languages': '🌐',
  'Humanities': '📜',
  'Social Sciences': '👥',
  'Technology & Computing': '💻',
  'Arts & Performance': '🎨',
  'Business & Finance': '💼',
  'Life Skills & Wellbeing': '🧘',
  'Christian Studies': '✝️',
  'Careers & Study Skills': '🧭',
  'Astronomy & Space': '🔭',
  'Elite Academy': '👑',
  'General': '📚',
  'Bible Journey': '📖',     // faith stream — seeker to scholar progression
  'Faith Stream': '✝️',      // fallback if group name ever differs
  'Electives & Practical Arts': '🌍',
};

const GROUP_ORDER = [
  'Elite Academy', 'Mathematics', 'Sciences', 'English & Writing', 'Languages', 'Humanities',
  'Social Sciences', 'Technology & Computing', 'Arts & Performance',
  'Business & Finance', 'Life Skills & Wellbeing', 'Christian Studies',
  'Bible Journey', // sits right after Christian Studies — Bible-adjacent but distinct
  'Careers & Study Skills', 'Astronomy & Space', 'Electives & Practical Arts', 'General',
];

const AGE_RANGES = [
  { key: 'all', label: 'All Ages', min: 0, max: 99 },
  { key: '5-7', label: '5–7', min: 5, max: 7 },
  { key: '8-11', label: '8–11', min: 8, max: 11 },
  { key: '12-14', label: '12–14', min: 12, max: 14 },
  { key: '15-18', label: '15–18', min: 15, max: 18 },
  { key: 'adult', label: 'Adult / All', min: 18, max: 99 },
];

export default function SoloSubjectsPage() {
  const { user, userProfile, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  // Org-flavored wording — resolves from the user's facility (main domain AND
  // org subdomain), falling back to facility_type for legacy hubs.
  const vocab = useOrgVocab(((userProfile as any)?.facility_org_type || userProfile?.facility_type) as any);

  const [classes, setClasses] = useState<MasterClass[]>([]);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [ageRange, setAgeRange] = useState('all');
  const [filterTrack, setFilterTrack] = useState<'all' | 'standard' | 'elite' | 'faith' | 'community'>('all');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [previewClass, setPreviewClass] = useState<MasterClass | null>(null);
  const [courseDetail, setCourseDetail] = useState<CourseDetail | null>(null);
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);

  const { elite, standard, isPaid } = getEntitlements(userProfile);
  const { isAdmin } = useAdmin();

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/signin', { replace: true });
      return;
    }

    // ── B11 member gate ──────────────────────────────────────────────────────
    // Child members (homeschool/family/school students) don't self-select subjects —
    // their parent/teacher assigns via /homeschool/child/:id/subjects. This page is
    // for solo learners and self-study parents only.
    // Platform (white-label) members are adult self-serve learners (employees,
    // participants, athletes) — not children awaiting assignment. Their seat
    // was enforced at invite time; they browse and pick their own courses.
    if (user && !authLoading && !isAdmin && userProfile?.facility_role === 'member' && userProfile?.facility_type !== 'platform') {
      navigate('/learn', { replace: true });
      return;
    }

    if (user) {
      loadData();
    }
  }, [user?.id, authLoading, userProfile?.facility_role]);

  async function loadData() {
    if (!user) return;
    setLoading(true);
    try {
      // 1. Fetch user's current enrollments
      const { data: enrolled } = await supabase
        .from('class_enrollments')
        .select('class_id')
        .eq('student_id', user.id);

      const enrolledIds = new Set((enrolled || []).map((e) => e.class_id));

      // 2. Fetch active catalog classes from BOTH hubs: Master (standard + elite)
      // + Faith (the 9-course Bible Journey stream). The null-curriculum_track
      // filter below already excludes the 45 university classes on the Faith hub.
      const { data: allClasses } = await supabase
        .from('classes')
        .select('id, name, description, target_age, category_id, curriculum_track, subject_group, lesson_count, module_count, categories(name)')
        .eq('is_active', true)
        .in('facility_id', ['00000000-0000-0000-0000-000000000001', '00000000-0000-0000-0000-000000000002'])
        .order('name');

      const mapped: MasterClass[] = (allClasses || [])
        .filter(c => {
          const n = c.name?.toLowerCase() || '';
          if (n.includes('pastoral ministry')) return false;
          if (!c.curriculum_track) return false;
          return true;
        })
        .map((c) => {
          const catName = (c.categories as any)?.name || 'General';
          const isFaithTrack = c.curriculum_track === 'faith';
          // Bible Journey is the canonical DB value for subject_group on these
          // rows, but keep this mapping as belt-and-braces against data drift.
          const displayGroup = isFaithTrack ? 'Bible Journey' : ((c as any).subject_group || 'General');
          return {
            id: c.id,
            name: c.name,
            description: c.description,
            target_age: c.target_age,
            category_id: c.category_id,
            category_name: catName,
            subject_group: displayGroup,
            is_enrolled: enrolledIds.has(c.id),
            curriculum_track: (c as any).curriculum_track || null,
            lesson_count: (c as any).lesson_count ? Number((c as any).lesson_count) : null,
            module_count: (c as any).module_count ? Number((c as any).module_count) : null,
            module_plan: null,
          };
        });

      // Community electives — free for everyone, published teacher-built
      // courses from the library view (seed facility + future teacher picks).
      const { data: community } = await supabase
        .from('community_course_library' as any)
        .select('id, name, description, target_age, category_id, module_count, subject_group')
        .limit(100)
      for (const c of (community ?? []) as any[]) {
        mapped.push({
          id: c.id,
          name: c.name,
          description: c.description,
          target_age: c.target_age,
          category_id: c.category_id,
          category_name: c.name,
          subject_group: 'Electives & Practical Arts',
          is_enrolled: enrolledIds.has(c.id),
          curriculum_track: 'community',
          lesson_count: null,
          module_count: c.module_count ? Number(c.module_count) : null,
          module_plan: null,
        })
      }

      setClasses(mapped);

      // Expand top groups by default
      const groups = new Set(mapped.map(c => c.subject_group));
      if (groups.size <= 6) {
        setExpandedGroups(groups);
      } else {
        setExpandedGroups(new Set(GROUP_ORDER.slice(0, 4)));
      }
    } catch (err) {
      console.error('[SoloSubjectsPage] Error loading catalog:', err);
    } finally {
      setLoading(false);
    }
  }

  function checkCanAccess(cls: MasterClass) {
    if (isAdmin) return true; // Platform admins browse and test all courses freely
    if (cls.curriculum_track === 'community') return true; // electives are free for everyone
    // Platform (white-label) orgs: the seat is the meter (enforced at invite
    // time) — catalog access rides facility membership, admins included free.
    if (userProfile?.facility_type === 'platform') return true;
    if (isFaith(cls)) return isPaid; // Bible Journey unlocks with ANY paid plan
    if (isElite(cls)) return elite;
    return standard;
  }

  async function enrollSelf(classId: string, categoryId: string) {
    if (!user) return;
    const targetCls = classes.find(c => c.id === classId);
    if (!targetCls) return;

    if (!checkCanAccess(targetCls)) {
      setShowSubscribeModal(true);
      return;
    }

    // Facility admins browse freely — the free 1-subject slot gate handles
    // entitlement checks per-subject. The subscribe modal only fires after
    // they attempt to enrol a second subject without a plan.
    // (Member gate above still redirects children to /learn.)

    setEnrolling(classId);
    try {
      // Write BOTH tables: class_enrollments AND student_subject_progress
      await supabase.from('class_enrollments').upsert({
        class_id: classId,
        student_id: user.id,
        mastery_score: 0,
        progress_state: { current_unit: 1, current_lesson: 1, total_lessons_completed: 0 },
      }, { onConflict: 'class_id,student_id' });

      await supabase.from('student_subject_progress').upsert({
        user_id: user.id,
        category_id: categoryId,
        class_id: classId,
        mastery_score: 0,
        current_unit: 1,
        current_lesson: 1,
      }, { onConflict: 'user_id,category_id,class_id' });

      setClasses(prev => prev.map(c => c.id === classId ? { ...c, is_enrolled: true } : c));
    } catch (err) {
      console.error('[SoloSubjectsPage] Error enrolling:', err);
    } finally {
      setEnrolling(null);
    }
  }

  async function unenrollSelf(classId: string) {
    if (!user) return;
    setEnrolling(classId);
    try {
      // Remove from BOTH tables
      await supabase.from('class_enrollments').delete().eq('class_id', classId).eq('student_id', user.id);
      await supabase.from('student_subject_progress').delete().eq('class_id', classId).eq('user_id', user.id);
      setClasses(prev => prev.map(c => c.id === classId ? { ...c, is_enrolled: false } : c));
    } catch (err) {
      console.error('[SoloSubjectsPage] Error unenrolling:', err);
    } finally {
      setEnrolling(null);
    }
  }

  const toggleGroup = (group: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      next.has(group) ? next.delete(group) : next.add(group);
      return next;
    });
  };

// Module-scoped plan cache — survives modal opens, resets with session/page reload
const planCache = new Map<string, any[]>();

  function buildCourseDetail(cls: MasterClass, plan?: any[] | null): CourseDetail {
    const track = cls.curriculum_track || 'foundation';

    const tierColor =
      track === 'advanced' ? '#C9A84C'
      : track === 'intermediate' ? '#7C6DFA'
      : isFaith(cls) ? '#8A6D3B'
      : cls.subject_group === 'Elite Academy' ? '#D4A843'
      : '#4A90D9';

    const badge =
      track === 'advanced' ? 'Advanced'
      : track === 'intermediate' ? 'Intermediate'
      : track === 'foundation' ? 'Foundation'
      : isFaith(cls) ? 'Bible Journey'
      : 'Standard';

    const emoji = GROUP_ICONS[cls.subject_group] || '📚';

    // Prefer DB plan; seed with Elite constant as first paint if DB plan not yet fetched
    let modules = ELITE_COURSE_DETAILS[cls.name] || [];
    const mp = plan || cls.module_plan;
    if (mp && mp.length > 0) {
      modules = (mp as any[]).map((m: any, mi: number) => ({
        module_id: m.module_id ?? mi + 1,
        title: m.title || m.name || `Module ${mi + 1}`,
        lessons: Array.isArray(m.lessons)
          ? m.lessons.map((l: any, li: number) => ({
              lesson_id: l.lesson_id ?? li + 1,
              title: typeof l === 'string' ? l : (l.title || l.name || `Lesson ${li + 1}`),
            }))
          : [],
      }));
    }

    return {
      name: cls.name,
      tierColor,
      badge,
      emoji,
      modules,
    };
  }

  const openDetail = async (cls: MasterClass) => {
    setPreviewClass(cls);
    const cached = planCache.get(cls.id);
    // Instant paint: cache if available, else elite seed or loading shimmer
    setCourseDetail(buildCourseDetail(cls, cached ?? null));

    if (cached) return; // Truth already verified from DB this session

    const table = cls.curriculum_track === 'community' ? 'community_course_library' : 'classes';
    const { data } = await (supabase.from(table as any) as any)
      .select('module_plan')
      .eq('id', cls.id)
      .maybeSingle();

    const plan = Array.isArray(data?.module_plan) ? data.module_plan : [];
    if (plan.length > 0) {
      planCache.set(cls.id, plan);
      setCourseDetail(buildCourseDetail(cls, plan));
    }
  };

  const filtered = useMemo(() => {
    const range = AGE_RANGES.find(r => r.key === ageRange) || AGE_RANGES[0];
    return classes.filter(c => {
      const q = search.toLowerCase();
      const matchSearch = !q || c.name.toLowerCase().includes(q) || c.category_name.toLowerCase().includes(q) || c.subject_group.toLowerCase().includes(q);
      const matchAge = ageRange === 'all' || !c.target_age ||
        isFaith(c) ||                                          // Bible Journey: never age-filtered
        (c.target_age >= range.min && c.target_age <= range.max);
      const matchTrack =
        filterTrack === 'all'
          ? true
          : filterTrack === 'elite'
            ? isElite(c)
            : filterTrack === 'faith'
              ? isFaith(c)
              : filterTrack === 'community'
                ? c.curriculum_track === 'community'
                : !isElite(c) && !isFaith(c) && c.curriculum_track !== 'community';
      return matchSearch && matchAge && matchTrack;
    });
  }, [classes, search, ageRange, filterTrack]);

  const enrolledFiltered = useMemo(() => {
    return [...filtered.filter(c => c.is_enrolled)].sort((a, b) => {
      if (a.subject_group === 'Bible Journey' && b.subject_group === 'Bible Journey') {
        return a.id.localeCompare(b.id) || a.name.localeCompare(b.name);
      }
      if (a.subject_group === 'Bible Journey') return -1;
      if (b.subject_group === 'Bible Journey') return 1;
      return a.name.localeCompare(b.name);
    });
  }, [filtered]);
  const availableFiltered = useMemo(() => filtered.filter(c => !c.is_enrolled), [filtered]);

  const groupedAvailable = useMemo(() => {
    const map = new Map<string, MasterClass[]>();
    for (const c of availableFiltered) {
      const arr = map.get(c.subject_group) || [];
      arr.push(c);
      map.set(c.subject_group, arr);
    }
    // Bible Journey IDs encode the canonical sequence (course-1 … course-9).
    // Sort by id for faith, alphabetically by name for all other groups.
    const sortItems = (g: string, items: MasterClass[]) =>
      g === 'Bible Journey'
        ? [...items].sort((a, b) => a.id.localeCompare(b.id))
        : [...items].sort((a, b) => a.name.localeCompare(b.name));
    return GROUP_ORDER
      .filter(g => map.has(g))
      .map(g => ({ group: g, items: sortItems(g, map.get(g)!) }))
      .concat(
        Array.from(map.entries())
          .filter(([g]) => !GROUP_ORDER.includes(g))
          .map(([group, items]) => ({ group, items: sortItems(group, items) }))
      );
  }, [availableFiltered]);

  const enrolledCount = classes.filter(c => c.is_enrolled).length;

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-background flex-col gap-4">
        <motion.div animate={{ rotate: 360 }} transition={{ duration: 1.2, repeat: Infinity, ease: 'linear' }}
          className="w-10 h-10 rounded-full border-[3px] border-muted border-t-primary" />
        <p className="text-muted-foreground text-sm">{t('learn.loading_course_catalogue')}</p>
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-background min-h-screen pb-16">
      {/* TOP BAR */}
      <div className="sticky top-0 z-50 bg-card/95 backdrop-blur-md border-b border-border">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => navigate('/learn')}
            className="text-muted-foreground hover:text-foreground text-sm transition-colors flex items-center gap-1.5"
          >
            <ArrowLeft className="w-4 h-4" /> Back to My Learning Journey
          </button>
          <span className="text-border">|</span>
          <h1 className="font-bold text-sm sm:text-base text-foreground truncate flex-1 text-white">
            {vocab.coursePlural} Catalogue
          </h1>
          <PlanBadge userProfile={userProfile} dark={false} />
          <div className="bg-primary/10 text-primary border border-primary/20 rounded-full px-3 py-1.5 text-xs font-bold whitespace-nowrap">
            <BookOpen className="w-3.5 h-3.5 inline mr-1" />
            {enrolledCount} Active Courses
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* HEADER */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
          <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-foreground mb-1.5 font-serif text-white">
            Explore All {vocab.coursePlural}
          </h2>
          <p className="text-muted-foreground text-sm max-w-2xl leading-relaxed">
            Browse our complete curriculum spanning 30 Elite Academy disciplines, 84 foundation & standard subjects,
            and Bible Journey, our 9-course faith stream, included as a bonus with any plan. Add any unlocked
            course to your active learning journey instantly.
          </p>
        </motion.div>

        {/* SEARCH & FILTERS */}
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
          className="bg-card rounded-2xl border border-border p-4 mb-6 space-y-3 shadow-sm">
          {/* Search bar */}
          <div className="relative">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              placeholder={t('learn.search_by_course_name_subject_group')}
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-input rounded-xl text-sm bg-background outline-none focus:border-primary focus:ring-1 focus:ring-primary/20 transition-all"
            />
          </div>

          {/* Filter Pills — Standard / Elite / Bible Journey stand as three equal schools */}
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <span className="text-xs font-semibold text-muted-foreground mr-1">{t('learn.stream')}</span>
            <button
              onClick={() => setFilterTrack('all')}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${filterTrack === 'all'
                ? 'bg-primary text-primary-foreground shadow-sm'
                : 'bg-muted text-muted-foreground hover:text-foreground'
                }`}
            >
              All
            </button>
            <button
              onClick={() => setFilterTrack('standard')}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1 ${filterTrack === 'standard'
                ? 'bg-primary text-primary-foreground shadow-sm'
                : 'bg-muted text-muted-foreground hover:text-foreground'
                }`}
            >
              📚 Standard (84)
            </button>
            <button
              onClick={() => setFilterTrack('elite')}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1 ${filterTrack === 'elite'
                ? 'bg-amber-600 text-white shadow-sm'
                : 'bg-muted text-muted-foreground hover:text-foreground'
                }`}
            >
              👑 Elite Academy (30)
            </button>
            <button
              onClick={() => setFilterTrack('faith')}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1 ${filterTrack === 'faith'
                ? 'bg-amber-800 text-white shadow-sm'
                : 'bg-muted text-muted-foreground hover:text-foreground'
                }`}
            >
              📖 Bible Journey (9)
            </button>
            <button
              onClick={() => setFilterTrack('community')}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1 ${filterTrack === 'community'
                ? 'bg-emerald-600 text-white shadow-sm'
                : 'bg-muted text-muted-foreground hover:text-foreground'
                }`}
            >
              🌍 Electives
            </button>

            <span className="text-border mx-1">|</span>

            {/* Age dropdown */}
            <select
              value={ageRange}
              onChange={e => setAgeRange(e.target.value)}
              className="px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-muted text-foreground border-none outline-none cursor-pointer"
            >
              {AGE_RANGES.map(r => (
                <option key={r.key} value={r.key}>{r.label}</option>
              ))}
            </select>
          </div>
        </motion.div>

        {/* SECTION 1: CURRENTLY ENROLLED */}
        {enrolledFiltered.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-3">
              <BookOpen className="w-4 h-4 text-primary" />
              <h3 className="text-sm font-bold uppercase tracking-wider text-foreground text-white">
                In Your Learning Journey ({enrolledFiltered.length})
              </h3>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {enrolledFiltered.map((cls) => (
                <div
                  key={cls.id}
                  onClick={() => openDetail(cls)}
                  className="flex items-center gap-3 p-3.5 bg-primary/5 border border-primary/20 rounded-xl hover:shadow-sm cursor-pointer transition-all"
                >
                  <span className="text-xl flex-shrink-0">{GROUP_ICONS[cls.subject_group] || '📚'}</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm text-foreground truncate">{cls.name}</div>
                    <div className="text-[11px] text-muted-foreground flex items-center gap-2 mt-0.5">
                      <span>{isFaith(cls) ? 'All ages · mature content' : (cls.target_age ? `Age ${cls.target_age}+` : 'All ages')}</span>
                      {isElite(cls) && (
                        <span className="bg-amber-100 text-amber-800 text-[9px] font-bold px-1.5 py-0.2 rounded">
                          ELITE
                        </span>
                      )}
                      {isFaith(cls) && (
                        <span className="bg-amber-50 text-amber-800 text-[9px] font-bold px-1.5 py-0.2 rounded">
                          📖 BIBLE JOURNEY
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); unenrollSelf(cls.id); }}
                    disabled={enrolling === cls.id}
                    title="Remove course from /learn"
                    className="p-1.5 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-lg transition-colors flex-shrink-0"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* SECTION 2: AVAILABLE CATALOGUE */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Crown className="w-4 h-4 text-amber-600" />
            <h3 className="text-sm font-bold uppercase tracking-wider text-foreground text-white">
              Available Courses ({availableFiltered.length})
            </h3>
          </div>

          <div className="space-y-3">
            {groupedAvailable.map(({ group, items }) => {
              const isExpanded = expandedGroups.has(group);
              const count = items.length;
              const isBibleJourneyGroup = group === 'Bible Journey';

              return (
                <div key={group} className="bg-card rounded-2xl border border-border overflow-hidden">
                  <button
                    onClick={() => toggleGroup(group)}
                    className="w-full flex items-center justify-between p-4 text-left hover:bg-muted/40 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-xl">{GROUP_ICONS[group] || '📚'}</span>
                      <div>
                        <div className="font-bold text-sm sm:text-base text-foreground flex items-center gap-2">
                          {group}
                          {isBibleJourneyGroup && (
                            <span className="text-[9px] font-bold bg-amber-50 text-amber-800 border border-amber-200 px-1.5 py-0.5 rounded-full whitespace-nowrap">
                              BONUS: INCLUDED WITH ANY PLAN
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground">{count} {count === 1 ? 'course' : 'courses'}</div>
                      </div>
                    </div>
                    <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                  </button>

                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden"
                      >
                        <div className="px-3.5 pb-3.5 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                          {items.map((cls) => {
                            const canAccess = checkCanAccess(cls);
                            const locked = !canAccess;

                            return (
                              <div
                                key={cls.id}
                                onClick={() => openDetail(cls)}
                                className={`flex items-center gap-3 p-3.5 rounded-xl border transition-all cursor-pointer ${locked
                                  ? 'bg-muted/30 border-dashed border-border opacity-75'
                                  : 'bg-background border-border hover:border-primary/40 hover:shadow-sm'
                                  }`}
                              >
                                <span className={`text-xl flex-shrink-0 ${locked ? 'grayscale' : ''}`}>
                                  {locked ? <Lock className="w-5 h-5 text-muted-foreground" /> : (GROUP_ICONS[cls.subject_group] || '📚')}
                                </span>
                                <div className="flex-1 min-w-0">
                                  <div className={`font-semibold text-sm leading-tight truncate ${locked ? 'text-muted-foreground' : 'text-foreground'}`}>
                                    {cls.name}
                                  </div>
                                  <div className="text-[11px] text-muted-foreground flex items-center gap-2 mt-0.5">
                                    <span>{isFaith(cls) ? 'All ages · mature content' : (cls.target_age ? `Age ${cls.target_age}+` : 'All ages')}</span>
                                    {isElite(cls) && (
                                      <span className="bg-amber-100 text-amber-800 text-[9px] font-bold px-1.5 rounded">
                                        👑 Elite
                                      </span>
                                    )}
                                  </div>
                                </div>

                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (locked) {
                                      navigate('/homeschool/subscription');
                                    } else {
                                      enrollSelf(cls.id, cls.category_id);
                                    }
                                  }}
                                  disabled={enrolling === cls.id}
                                  className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-bold transition-all disabled:opacity-50 ${locked
                                    ? 'bg-amber-500 text-white hover:bg-amber-600'
                                    : 'bg-primary text-primary-foreground hover:opacity-90'
                                    }`}
                                >
                                  {locked ? 'Upgrade' : '+ Add'}
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* COURSE DETAIL MODAL */}
      <CourseDetailModal
        course={courseDetail}
        onClose={() => { setPreviewClass(null); setCourseDetail(null); }}
        onEnrol={previewClass ? (
          previewClass.is_enrolled
            ? () => { unenrollSelf(previewClass.id); setPreviewClass(null); setCourseDetail(null); }
            : !checkCanAccess(previewClass)
              ? () => { setPreviewClass(null); setShowSubscribeModal(true); }
              : () => { enrollSelf(previewClass.id, previewClass.category_id); setPreviewClass(null); setCourseDetail(null); }
        ) : undefined}
      />

      {/* UPGRADE MODAL */}
      <AnimatePresence>
        {showSubscribeModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setShowSubscribeModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={e => e.stopPropagation()}
              className="bg-card rounded-2xl border border-border p-6 max-w-md w-full shadow-2xl space-y-4"
            >
              <div className="w-12 h-12 rounded-full bg-amber-500/10 text-amber-600 flex items-center justify-center mx-auto text-2xl">
                👑
              </div>
              <div className="text-center space-y-1.5">
                <h3 className="text-lg font-bold text-foreground text-white">
                  {elite && !standard
                    ? 'Upgrade to Complete Bundle'
                    : 'Unlock Full Course Access'}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {elite && !standard
                    ? 'Your Elite Academy plan includes all 30 classical mentorship courses and Bible Journey. Upgrade to the Complete Bundle to unlock all 84 Standard subjects.'
                    : standard && !elite
                      ? 'Upgrade to the Elite Academy or Complete Bundle to unlock all 30 leadership and classical oratory courses.'
                      : 'Subscribe to a Still Waters Academy plan to access this course, and Bible Journey comes included, and track your mastery.'}
                </p>
              </div>
              <div className="flex gap-2 pt-2">
                <button
                  onClick={() => setShowSubscribeModal(false)}
                  className="flex-1 px-4 py-2.5 rounded-xl border border-border text-sm font-semibold text-muted-foreground hover:text-foreground"
                >
                  Close
                </button>
                <button
                  onClick={() => navigate('/homeschool/subscription')}
                  className="flex-1 px-4 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-600 text-white text-sm font-bold shadow-sm"
                >
                  View Plans
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
} 